import { useState } from 'react';
import { Ban, Loader2, AlertTriangle } from 'lucide-react';
import { Button, Modal, ModalBody, ModalFooter, FormField, Textarea, useToast } from '@afrilink/ui';
import { salesService, type LeadQuotation } from '@/domains/sales/services/salesService';

/**
 * WithdrawQuotationButton — take a live quotation off the table.
 *
 * WHY THIS EXISTS
 * Until 2026-08-05 the only way to retire a quotation was to issue a
 * replacement with `supersede_quotation_id`, and `IssueQuotationModal` could
 * only offer the ONE quotation `v_sales_lead_order` returned (a LATERAL … LIMIT
 * 1 over the latest order). A lead holding several therefore had older
 * quotations that were live, payable, and unreachable — info@lionroar.group
 * held three at once, the oldest a R500 Studio deposit for a product we had
 * decided not to sell him.
 *
 * WITHDRAW, NOT DELETE
 * The wording is deliberate. A quotation carries a sequenced QUO- number and
 * stays on record as `cancelled` — nothing is erased. Saying "Delete" would
 * promise something the system does not (and should not) do.
 *
 * Shared by SalesQuotationsPage and SalesLeadDetailPage so the two never drift,
 * matching the QuotationPayLinkButton precedent.
 */
export function WithdrawQuotationButton({
  quotation,
  onWithdrawn,
  size = 'sm',
  label = 'Withdraw',
  fullWidth = false,
}: {
  quotation: Pick<LeadQuotation, 'invoice_id' | 'quote_number' | 'amount'> & {
    plan_name?: string | null;
    sku_code?: string | null;
    domain_name?: string | null;
  };
  /** Called after a successful withdrawal so the caller can refresh its list. */
  onWithdrawn: () => void;
  size?: 'sm' | 'default';
  label?: string;
  fullWidth?: boolean;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const what =
    quotation.plan_name ??
    quotation.sku_code ??
    'this quotation';

  async function submit() {
    setBusy(true);
    try {
      const res = await salesService.cancelQuotation({
        invoice_id: quotation.invoice_id,
        reason: reason.trim() || undefined,
      });
      toast({
        tone: 'success',
        title: `${res.quote_number} withdrawn`,
        body:
          res.service_orders_cancelled > 0
            ? `The customer's pay link no longer works. ${res.service_orders_cancelled} order${res.service_orders_cancelled === 1 ? '' : 's'} cancelled with it.`
            : 'The customer’s pay link no longer works.',
      });
      setOpen(false);
      setReason('');
      onWithdrawn();
    } catch (e) {
      // Surface the EF's own message — 'quotation_not_live' after a payment
      // landed mid-flight is a legitimate outcome the agent needs to read.
      toast({
        tone: 'warning',
        title: 'Could not withdraw',
        body: (e as Error).message,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        size={size}
        variant="ghost"
        onClick={() => setOpen(true)}
        className={fullWidth ? 'w-full text-danger hover:text-danger' : 'text-danger hover:text-danger'}
        title="Withdraw this quotation so the customer can no longer pay it"
        aria-label={`Withdraw quotation ${quotation.quote_number}`}
      >
        <Ban className="h-4 w-4" />
        {label}
      </Button>

      {open && (
        <Modal
          open
          onOpenChange={(o) => { if (!o && !busy) { setOpen(false); setReason(''); } }}
          title={`Withdraw ${quotation.quote_number}?`}
          description="The customer's payment link stops working immediately. The quotation stays on record as cancelled — nothing is deleted."
          size="wide"
        >
          <ModalBody>
            <div className="mb-4 rounded border border-border bg-muted/30 p-3 text-sm">
              <div className="font-medium text-foreground">{what}</div>
              {quotation.domain_name && (
                <div className="text-xs text-muted-foreground">{quotation.domain_name}</div>
              )}
              {quotation.amount !== null && quotation.amount !== undefined && (
                <div className="mt-1 tabular-nums text-muted-foreground">
                  {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(
                    Number(quotation.amount)
                  )}
                </div>
              )}
            </div>

            <FormField
              label="Why is this being withdrawn?"
              helper="Optional, but it is what the audit trail will show. e.g. “Replaced by Entry quote — customer declined Studio.”"
            >
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                autoFocus
                placeholder="Replaced by a different package / customer no longer proceeding / quoted in error"
              />
            </FormField>

            <div className="mt-4 flex items-start gap-2 rounded border border-warning/30 bg-warning/5 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <p className="text-muted-foreground">
                If the customer has already paid this quotation, withdrawal will be refused — a
                paid document has to be refunded instead, not withdrawn.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setReason(''); }} disabled={busy}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={submit} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Withdraw quotation
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
